import { compareDesc, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Grid, Type } from '../../app/StyleGuide';
import { ErrorMessage } from '../../components/AlertMessages';
import { PrimaryButton } from '../../components/Buttons';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, FullWidthCard, Image, InlineList } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import useCurriculum from '../../hooks/useCurriculum';
import useQueryParams from '../../hooks/useQueryParams';
import windowService from '../../services/windowService';
import { useUser } from '../authentication';
import { getAdvancePlanningMaterials } from './api/getAdvancePlanningMaterials';
import { HeaderNavigation } from './components/HeaderNavigation';
import { VolumeHeadersSection } from './components/VolumeHeadersSection';

const VolumeName = styled.h3`
  margin: 0 0 ${Grid._3};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._5};
`;

const FullWidthCardContainer = styled(FullWidthCard)`
  padding-top: ${Grid._5};
`;

const SubscribeInfoBox = styled(HelpAndInfoBox)`
  gap: ${Grid._5};

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  i {
    display: none;
  }
`;

const VolumeList = ({ volumes, scrollId }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollOffset = 140;

      window.requestAnimationFrame(() => {
        window.scrollTo({ behavior: 'smooth', top: scrollRef.current.offsetTop - scrollOffset });
      });
    }
  }, []);

  return volumes.map(volume => (
    <div
      key={volume._id}
      className="grid-container grid-col-4 grid-sm-col-8 grid-gap-32"
      style={{ paddingBottom: Grid._10 }}
      data-tracking-component={'volume'}
      data-tracking-id={volume.name}
      id={volume._id}
      ref={scrollId === volume._id ? scrollRef : null}
    >
      <div className="grid-col-span-4 grid-sm-col-span-2">
        <Image src={volume.imageUrl} />
      </div>
      <div className="grid-col-span-4 grid-sm-col-span-6">
        <VolumeName>{volume.name}</VolumeName>
        <InlineList content={'•'} fontSize="16px">
          {volume.number && <li data-qa-hook="volumeNumber">Volume {volume.number}</li>}
          {volume.periodTitle && <li data-qa-hook="quarterYear"> {volume.periodTitle} </li>}
        </InlineList>
        {volume.sections.map(section =>
          section.materials.length ? (
            <VolumeHeadersSection
              key={section.id}
              ref={scrollId === section.id ? scrollRef : null}
              section={section}
              initializeOpen={scrollId == section.id}
            />
          ) : null
        )}
      </div>
    </div>
  ));
};

export function ManageBibleStudyAdvancePlanning() {
  const { user } = useUser();
  const [data, setData] = useState({ isLoading: true });
  const { brand, ageCategory } = useCurriculum();
  const { curriculumId } = useParams();
  const { volumeId, materialId } = useQueryParams({ preserveParams: true });

  const [crumbs] = useState([
    { name: 'Curriculum', route: '#/bible-studies' },
    { name: `${brand.name}: ${ageCategory}` },
  ]);

  const navigateToSubscription = () => windowService.redirectTo(`/subscribe-bible-study/${brand.code}/${curriculumId}`);

  useEffect(() => {
    getAdvancePlanningMaterials(curriculumId)
      .then(volumes => {
        const unlockedVolumes = volumes.filter(v => !v.locked);
        const lockedVolumes = volumes.filter(v => v.locked);

        setData({
          isLoading: false,
          volumes: unlockedVolumes.sort((a, b) => compareDesc(parseISO(a.startDate), parseISO(b.startDate))),
          hasLockedVolumes: lockedVolumes.length > 0,
        });
      })
      .catch(reason => {
        console.error(reason);
        setData({ isLoading: false, isError: true });
      });
  }, []);

  return (
    <>
      <HeaderNavigation
        crumbs={crumbs}
        brandCode={brand.code}
        ageCategory={ageCategory}
        curriculumId={curriculumId}
        orgId={user?.lastSelectedAccount}
        activeTab="advance-planning"
      />
      <FullWidthCardContainer>
        <Container>
          {data.isLoading ? (
            <LoadingState />
          ) : data.isError ? (
            <ErrorMessage>
              A problem occurred showing this page. Please refresh the page to try again.{' '}
              <a href="#/help">Contact Us</a>
            </ErrorMessage>
          ) : (
            <>
              {data.hasLockedVolumes && (
                <SubscribeInfoBox
                  title="Unlock more content to reach the next generation"
                  description="Some volumes are hidden because your trial comes to an end soon. Subscribe today for uninterrupted access to media-rich resources geared to engage the next generation with
            the Gospel."
                >
                  <PrimaryButton onClick={navigateToSubscription}>View Subscription Options</PrimaryButton>
                </SubscribeInfoBox>
              )}
              <VolumeList volumes={data.volumes} scrollId={materialId || volumeId || undefined} />
            </>
          )}
        </Container>
      </FullWidthCardContainer>
    </>
  );
}
